import { useState, useEffect, useCallback } from 'react';
import api from 'src/api/api';
import { HEADERS } from 'src/constants/headers';

const urlBase64ToUint8Array = (base64String: string) => {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding)
        .replace(/\-/g, '+')
        .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
};

export const useWebPush = (userId: string) => {
    const [subscription, setSubscription] = useState<PushSubscription | null>(
        null
    );
    const [isSupported, setIsSupported] = useState<boolean>(false);
    const [permission, setPermission] = useState<string>('default');

    useEffect(() => {
        if (
            'serviceWorker' in navigator &&
            'PushManager' in window &&
            'Notification' in window
        ) {
            setIsSupported(true);
            setPermission(Notification.permission);
        }
    }, []);

    const requestPermission = useCallback(async () => {
        if (!isSupported) {
            throw new Error('Web Push not supported in this browser');
        }

        try {
            const permission = await Notification.requestPermission();
            setPermission(permission);
            return permission;
        } catch (error) {
            throw error;
        }
    }, [isSupported]);

    const subscribe = useCallback(async () => {
        if (!isSupported) {
            throw new Error('Web Push not supported in this browser');
        }

        if (permission !== 'granted') {
            const newPermission = await requestPermission();
            if (newPermission !== 'granted') {
                throw new Error('Notification permission denied');
            }
        }
        try {
            const registration = await navigator.serviceWorker.ready;
            const existingSubscription =
                await registration.pushManager.getSubscription();

            if (existingSubscription) {
                setSubscription(existingSubscription);
                return existingSubscription;
            }

            const response = await api.get(
                '/notifications/push/public-key',
                HEADERS()
            );
            const publicKey =
                response.data.data?.publicKey || response.data.publicKey;

            if (!publicKey) {
                throw new Error('Public key not found in server response');
            }

            const subscription = await registration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: urlBase64ToUint8Array(publicKey),
            });

            setSubscription(subscription);

            if (userId) {
                try {
                    const p256dhKey = subscription.getKey('p256dh');
                    const authKey = subscription.getKey('auth');

                    if (!p256dhKey || !authKey) {
                        throw new Error(
                            'Notification keys not found in subscription'
                        );
                    }

                    await api.post(
                        '/notifications/push/subscribe',
                        {
                            userId,
                            subscription: {
                                endpoint: subscription.endpoint,
                                keys: {
                                    p256dh: btoa(
                                        String.fromCharCode.apply(
                                            null,
                                            Array.from(
                                                new Uint8Array(p256dhKey)
                                            )
                                        )
                                    ),
                                    auth: btoa(
                                        String.fromCharCode.apply(
                                            null,
                                            Array.from(new Uint8Array(authKey))
                                        )
                                    ),
                                },
                            },
                        },
                        HEADERS()
                    );
                } catch (error) {
                    throw error;
                }
            }

            return subscription;
        } catch (error) {
            throw error;
        }
    }, [isSupported, permission, requestPermission, userId]);

    const unsubscribe = useCallback(async () => {
        if (!subscription) {
            return;
        }

        try {
            await subscription.unsubscribe();
            setSubscription(null);

            if (userId) {
                await api.post(
                    '/notifications/push/unsubscribe',
                    {
                        userId,
                        endpoint: subscription.endpoint,
                    },
                    HEADERS()
                );
            }
        } catch (error) {
            throw error;
        }
    }, [subscription, userId]);

    const checkSubscription = useCallback(async () => {
        if (!isSupported) {
            return;
        }

        try {
            const registration = await navigator.serviceWorker.ready;
            const existingSubscription =
                await registration.pushManager.getSubscription();
            setSubscription(existingSubscription);
        } catch (error) {
            throw error;
        }
    }, [isSupported]);

    useEffect(() => {
        if (isSupported && userId) {
            checkSubscription();
        }
    }, [isSupported, userId, checkSubscription]);

    return {
        subscription,
        isSupported,
        permission,
        requestPermission,
        subscribe,
        unsubscribe,
        checkSubscription,
    };
};
